import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { Loader2 } from 'lucide-react';
import { firstNameOf } from '../lib/format';
import { Avatar } from '../components/Avatar';
import { rememberInvite, forgetInvite } from '../lib/pendingInvite';
import { toast } from 'sonner';

export const Invite = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();
  const [inviter, setInviter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [alreadyConnected, setAlreadyConnected] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await api.getInvite(code);
        if (!active) return;
        setInviter(data.inviter);
        // Signed-out visitors keep the invitation through sign-in / a refresh.
        if (!isAuthenticated) rememberInvite(code, data.inviter?.name);
      } catch (err) {
        if (active) {
          setInvalid(true);
          forgetInvite();
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [code, isAuthenticated]);

  // A signed-in member is told up front if they are already connected.
  useEffect(() => {
    let active = true;
    if (!isAuthenticated || !inviter || !user || inviter.id === user.id) return undefined;
    (async () => {
      try {
        const connections = await api.getMyConnections();
        if (active && connections?.some((c) => c.id === inviter.id)) setAlreadyConnected(true);
      } catch (err) {
        // Non-blocking: the accept call is the authority on this.
      }
    })();
    return () => { active = false; };
  }, [isAuthenticated, inviter, user]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const result = await api.acceptInvite(code);
      forgetInvite();
      if (result?.status === 'already_connected') {
        setAlreadyConnected(true);
        return;
      }
      navigate('/connected', { replace: true, state: { inviterName: inviter?.name } });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      forgetInvite();
      if (status === 409) {
        setAlreadyConnected(true);
      } else if (status === 400 || status === 404) {
        setInvalid(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not connect right now');
      }
    } finally {
      setConnecting(false);
    }
  };

  if (loading || isLoading) {
    return (
      <div className="page invite-page">
        <div className="loading-state"><Loader2 className="loading-spinner" /></div>
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="page invite-page" data-testid="invite-invalid">
        <div className="invite-recipient">
          <span className="welcome-brand">irecommend</span>
          <h1 className="invite-recipient-title">This invitation isn&apos;t valid</h1>
          <p className="invite-recipient-sub">
            It may have already been used. Ask for a new invite link.
          </p>
          <button className="welcome-btn" onClick={() => navigate('/')} data-testid="invite-invalid-home">
            Back to irecommend
          </button>
        </div>
      </div>
    );
  }

  const first = firstNameOf(inviter?.name);
  const isOwnInvite = isAuthenticated && user && inviter?.id === user.id;

  return (
    <div className="page invite-page" data-testid="invite-page">
      <div className="invite-recipient">
        <span className="welcome-brand">irecommend</span>
        <Avatar
          userId={inviter?.id}
          name={inviter?.name}
          photoUrl={inviter?.photoUrl}
          className="identity-avatar"
          testId="invite-avatar"
        />

        {isOwnInvite ? (
          <>
            <h1 className="invite-recipient-title" data-testid="invite-own-link">
              This is your own invite link
            </h1>
            <p className="invite-recipient-sub">
              Send it to someone you trust. It still works, and it hasn&apos;t been used.
            </p>
            <button className="welcome-btn" onClick={() => navigate('/invite-people')} data-testid="invite-own-share">
              Back to invites
            </button>
          </>
        ) : alreadyConnected ? (
          <>
            <h1 className="invite-recipient-title" data-testid="invite-already-connected">
              You&apos;re already connected with {first}
            </h1>
            <p className="invite-recipient-sub">
              Their recommendations are already in your feed. This invitation is still
              unused, so {first} can share it with someone else.
            </p>
            <button className="welcome-btn" onClick={() => navigate('/')} data-testid="invite-already-home">
              Go to your feed
            </button>
          </>
        ) : isAuthenticated ? (
          <>
            <h1 className="invite-recipient-title" data-testid="invite-message">
              {first} invited you to connect
            </h1>
            <p className="invite-recipient-sub">
              Connect to share and discover recommendations with {first}.
            </p>
            <button
              className="welcome-btn"
              onClick={handleConnect}
              disabled={connecting}
              data-testid="connect-with-inviter"
            >
              {connecting ? 'Connecting…' : `Connect with ${first}`}
            </button>
          </>
        ) : (
          <>
            <h1 className="invite-recipient-title" data-testid="invite-message">
              {first} invited you to irecommend
            </h1>
            <p className="invite-recipient-sub">
              Join {first} to share and discover recommendations from people you trust.
            </p>
            <button
              className="welcome-btn"
              onClick={() => navigate('/signin', {
                state: { inviteCode: code, inviterName: inviter?.name, from: '/' },
              })}
              data-testid="accept-invite"
            >
              Join irecommend
            </button>
            <button
              className="invite-secondary-link"
              onClick={() => navigate('/signin', {
                state: { inviteCode: code, inviterName: inviter?.name, from: '/', mode: 'signin' },
              })}
              data-testid="invite-existing-member"
            >
              Already a member? Sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
};
